import React, { ForwardedRef, forwardRef, Fragment, PropsWithChildren } from 'react';
import { CheckboxGroup, ErrorMessage, Input, InputGroup, Label, RadioCheckboxGroup, RadioGroup, Select, Textarea, TokenInput } from '../index';
import { ICheckboxGroupField, IInputField, IInputGroupField, IRadioCheckboxGroupField, IRadioGroupField, ISelectField, ITextareaField, ITokenInputField, IWrapper } from './types';

const InputWrapper = ({ children, error, id, label, message, required }: PropsWithChildren<IWrapper>) => {
  const Wrapper = label ? Label : Fragment;
  const wrapperProps = label ? { 'className': 'w-100 position-relative mb-4 mb-lg-auto', 'data-testid': 'inputfield-wrapper', id } : {};

  return (
    <Wrapper {...wrapperProps}>
      {label && (
        <span>
          {label}
          {required && <span className="text-danger pb-0">*</span>}
        </span>
      )}
      {children}
      {error?.hasError && <ErrorMessage>{error?.message || ''}</ErrorMessage>}
      {message && !error?.hasError && (
        <div className="position-absolute" data-testid="inputfield-message" style={{ fontSize: '80%' }}>
          {message}
        </div>
      )}
    </Wrapper>
  );
};

const CheckboxGroupField = ({ children, error, message, ...rest }: ICheckboxGroupField) => (
  <InputWrapper {...{ children, error, message, ...rest }}>
    <CheckboxGroup {...rest} />
  </InputWrapper>
);

const InputGroupField = ({ error, ...rest }: IInputGroupField) => (
  <InputWrapper {...{ error, ...rest }}>
    <InputGroup error={error?.hasError} {...rest} />
  </InputWrapper>
);

const InputField = forwardRef(({ children, error, ...rest }: IInputField, ref: ForwardedRef<HTMLInputElement>) => (
  <InputWrapper {...{ children, error, ...rest }}>
    <Input error={error?.hasError} {...{ ref, ...rest }} />
  </InputWrapper>
));

const RadioGroupField = ({ children, error, id, label, message, name, required, ...rest }: IRadioGroupField) => (
  <InputWrapper {...{ children, error, id, label, message, name, required }}>
    <RadioGroup {...rest} />
  </InputWrapper>
);

const RadioCheckboxGroupField = ({ children, error, id, label, message, name, required, ...rest }: IRadioCheckboxGroupField) => (
  <InputWrapper {...{ children, error, id, label, message, name, required }}>
    <RadioCheckboxGroup error={error?.hasError} {...rest} />
  </InputWrapper>
);

const SelectField = ({ children, error, ...rest }: ISelectField) => (
  <InputWrapper {...{ children, error, ...rest }}>
    <Select error={error?.hasError} {...rest} />
  </InputWrapper>
);

const TextareaField = forwardRef(({ children, error, ...rest }: ITextareaField, ref: ForwardedRef<HTMLTextAreaElement>) => (
  <InputWrapper {...{ children, error, ...rest }}>
    <Textarea error={error?.hasError} {...{ ref, ...rest }} />
  </InputWrapper>
));

const TokenInputField = ({ children, error, ...rest }: ITokenInputField) => (
  <InputWrapper {...{ children, error, ...rest }}>
    <TokenInput error={error?.hasError} {...rest} />
  </InputWrapper>
);

export { CheckboxGroupField, InputField, InputGroupField, InputWrapper, RadioCheckboxGroupField, RadioGroupField, SelectField, TextareaField, TokenInputField };
